import { useTranslation } from 'react-i18next';

import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { Badge } from '@/components/ui/badge';
import { StatusDot, type StatusTone } from '@/components/efs/StatusDot';
import { cn } from '@/lib/utils';
import type { StagingRowDto, StagingRowStatus, ValidationSeverity } from '../types';

interface StagingRowTableProps {
  rows: StagingRowDto[];
  isLoading?: boolean;
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  pageSizeOptions?: number[];
  onRowClick: (row: StagingRowDto) => void;
}

const statusVariant: Record<StagingRowStatus, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    pending: 'secondary',
    valid: 'default',
    warning: 'outline',
    error: 'destructive',
    corrected: 'default',
    ignored: 'secondary',
    promoted: 'default',
  };

const severityRank: Record<ValidationSeverity, number> = {
  info: 0,
  warning: 1,
  error: 2,
  critical: 3,
};

const severityTone: Record<ValidationSeverity, StatusTone> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  critical: 'critical',
};

const topSeverity = (row: StagingRowDto): ValidationSeverity | null => {
  if (row.validation_messages.length === 0) return null;
  return row.validation_messages.reduce<ValidationSeverity>(
    (best, m) => (severityRank[m.severity] > severityRank[best] ? m.severity : best),
    'info',
  );
};

const extractField = (payload: Record<string, unknown> | null, ...keys: string[]): string => {
  if (!payload) return '';
  for (const key of keys) {
    const v = payload[key];
    if (v !== undefined && v !== null && v !== '') {
      return typeof v === 'string' ? v : String(v);
    }
  }
  return '';
};

export const StagingRowTable = ({
  rows,
  isLoading,
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  pageSizeOptions,
  onRowClick,
}: StagingRowTableProps) => {
  const { t } = useTranslation();

  const columns: MasterTableColumn<StagingRowDto>[] = [
    {
      key: 'source_sheet_name',
      header: t('efs.imports.review.fields.source_sheet_name', { ns: 'imports' }),
      cell: (row) => <span className="font-mono text-xs">{row.source_sheet_name ?? '—'}</span>,
    },
    {
      key: 'source_row_number',
      header: t('efs.imports.review.fields.source_row_number', { ns: 'imports' }),
      cell: (row) => <span className="font-mono text-xs">{row.source_row_number ?? '—'}</span>,
      align: 'right',
    },
    {
      key: 'lot_code',
      header: t('efs.imports.review.fields.lot_code', { ns: 'imports' }),
      cell: (row) => (
        <span className="font-mono text-xs">
          {extractField(row.raw_payload, 'lot_code', 'inbound_lot_code') || '—'}
        </span>
      ),
    },
    {
      key: 'raw_item_name',
      header: t('efs.imports.review.fields.raw_item_name', { ns: 'imports' }),
      cell: (row) =>
        extractField(row.raw_payload, 'raw_incoming_item_name', 'raw_material_name', 'item_name') ||
        '—',
    },
    {
      key: 'kg',
      header: t('efs.imports.review.fields.kg', { ns: 'imports' }),
      align: 'right',
      cell: (row) => (
        <span className="font-mono tabular-nums">{extractField(row.raw_payload, 'kg')}</span>
      ),
    },
    {
      key: 'status',
      header: t('efs.imports.review.fields.status', { ns: 'imports' }),
      cell: (row) => (
        <Badge variant={statusVariant[row.status]}>
          {t(`efs.imports.row_statuses.${row.status}`, {
            ns: 'imports',
            defaultValue: row.status,
          })}
        </Badge>
      ),
    },
    {
      key: 'severity',
      header: t('efs.imports.review.fields.severity', { ns: 'imports' }),
      cell: (row) => {
        const sev = topSeverity(row);
        if (!sev) return <span className="text-efs-text-muted">—</span>;
        return (
          <StatusDot
            tone={severityTone[sev]}
            label={t(`efs.imports.severities.${sev}`, {
              ns: 'imports',
              defaultValue: sev,
            })}
          />
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <button
          type="button"
          className={cn(
            'rounded-md border border-efs-border bg-transparent px-2 py-1 text-xs font-medium text-efs-text-primary hover:bg-efs-bg-subtle',
          )}
          onClick={() => onRowClick(row)}
          aria-label={t('efs.imports.review.actions.open_row', { ns: 'imports' })}
        >
          {t('efs.imports.review.actions.open_row', { ns: 'imports' })}
        </button>
      ),
    },
  ];

  return (
    <MasterTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.staging_row_id}
      isLoading={isLoading}
      emptyMessage={t('efs.imports.review.empty', { ns: 'imports' })}
      pagination={{ page, perPage, total, onPageChange, onPerPageChange, pageSizeOptions }}
    />
  );
};
